'use client'

import styles from './MenuSidebarDefault.module.scss'
import { useSidebar } from '@/context/SidebarContext'
import { Link } from '@/components/Link'
import Icon from '@/components/Icon/Icon'
import { clsx } from 'clsx'
import { Image } from '@/components/Image'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import { useEffect, useRef } from 'react'

function buildLevels(
  items: any[],
  levels: { title: string | undefined; items: any[] }[] = [],
  title: string | undefined = undefined,
) {
  levels.push({
    title,
    items,
  })
  items.forEach((current) => {
    if (!current.children) return
    levels = buildLevels(current.children, levels, current.title)
  })

  return levels
}

const renderBanner = (item: any, isActive: boolean) => {
  const items = item.children?.filter((child: any) => child.image) ?? []

  return (
    <div className={styles['banner-wrapper']} key={item.id}>
      {item.title && <div className={styles['banner-title']}>{item.title}</div>}
      <Swiper
        className={clsx(styles['root-level'], styles['banner-level'])}
        key={String(isActive)}
        spaceBetween={16}
        slidesPerView={2}
      >
        {items.map((child: any, index: number) => (
          <SwiperSlide key={index}>
            <Link href={child.url} target={child.target} className={styles['image-link']}>
              <Image
                image={child.image}
                alt={child.title}
                cropOverride="Img_3_4"
                defaultWidth={600}
                defaultHeight={800}
              />
            </Link>
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  )
}

const renderLinks = (item: any) => {
  if (!item.children) {
    return null
  }

  return (
    <div className={clsx(styles['root-level'], styles['links-level'])} key={item.id}>
      {item.children.map((child: any, index: number) => {
        const arrow = child.fields?.arrow === 'true'

        return (
          <Link href={child.url} target={child.target} arrow={arrow} className={styles['static-link']} key={index}>
            {child.title}
            {item.fields?.adLabel === 'true' && <div className={styles['sponsored-label']}>Anzeige</div>}
          </Link>
        )
      })}
    </div>
  )
}

export function MenuSidebarDefaultTree({ sidebarId, items }: { sidebarId: string; items: any[] }) {
  const { path, goForward } = useSidebar()
  const currentPath = path(sidebarId)
  const treeRef = useRef<HTMLDivElement>(null)

  // Root level is just the categorization into spaces, so the first child level has to be removed
  const levels = buildLevels(items).filter((item) =>
    item.items.find((child) => (child.isRoot && child.level === sidebarId) || !child.isRoot),
  )
  const activeLevel = currentPath

  const forward = (id: string, path: string) => {
    // @ts-ignore
    treeRef.current?.parentNode?.scrollTo({ top: 0 })
    goForward(id, path)
  }

  return (
    <div className={styles.tree} ref={treeRef}>
      {levels.map((items, idx) => (
        <TreeLevel
          key={idx}
          title={items.title}
          items={items.items}
          activeLevel={activeLevel}
          sidebarId={sidebarId}
          goForward={forward}
        />
      ))}
    </div>
  )
}

const renderLink = (item: any, goForward: any, sidebarId: string) => {
  if (!item.children) {
    return (
      <Link
        href={item.url}
        target={item.target}
        className={clsx(styles['level-link'], item.isRoot && styles['root-level-link'])}
        key={item.id}
      >
        {item.title}
        {item.fields?.adLabel === 'true' && <div className={styles['sponsored-label']}>Anzeige</div>}
      </Link>
    )
  }
  return (
    <button
      className={clsx(styles['level-link'], item.isRoot && styles['root-level-link'])}
      onClick={() => goForward(sidebarId, item.id)}
      key={item.id}
    >
      <span>{item.title}</span>
      <Icon name="chevronRight" size={24} />
    </button>
  )
}

function TreeLevel({ items, title, activeLevel, sidebarId, goForward }: any) {
  const isActive = (items[0] ?? {}).level === activeLevel || (!activeLevel && (items[0] ?? {}).isRoot)
  return (
    <div className={clsx(styles.level, isActive ? styles.active : styles.hidden)}>
      <div>
        {title && <div className={styles['level-title']}>{title}</div>}
        {items?.map((item: any, index: number) => {
          if (item.isRoot) {
            if (item.fields?.type === 'banner') {
              return renderBanner(item, isActive)
            } else if (item.fields?.type === 'links') {
              return renderLinks(item)
            }
            return (
              <div key={index} className={styles['root-level']}>
                {item.children
                  ? item.children?.map((child: any) => {
                      return renderLink(child, goForward, sidebarId)
                    })
                  : renderLink([item], goForward, sidebarId)}
              </div>
            )
          }

          return renderLink(item, goForward, sidebarId)
        })}
      </div>
    </div>
  )
}
